import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import {
  BookOpen,
  Compass,
  User,
  LogOut,
  LayoutDashboard,
  PenTool,
  DollarSign,
  Sparkles,
  Users,
  ShoppingBag,
  Bell,
  Plus
} from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [earningsEnabled, setEarningsEnabled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

  // Fetch feature settings to check if earnings is enabled
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings/features`)
      .then(res => setEarningsEnabled(res.data.earnings_enabled || false))
      .catch(() => setEarningsEnabled(false));
  }, []);

  // Poll unread notification count
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const fetchUnread = () => {
      axios.get(`${BACKEND_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => setUnreadCount(res.data.unread_count || 0))
        .catch(() => { }); // silent fail
    };

    fetchUnread(); // immediate
    const interval = setInterval(fetchUnread, 30000); // every 30s
    return () => clearInterval(interval);
  }, [user, BACKEND_URL]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const libraryHref = user ? '/dashboard' : '/login';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-purple-500/20 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative flex items-center overflow-hidden rounded-xl">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-cyan-400 opacity-30 blur-md" />

              <img src="/favicon_new.png" className="relative z-10 w-7 h-7 top-1" alt="Logo" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 via-violet-400 to-cyan-400 bg-clip-text text-transparent">
              ComicMenia
            </span>
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-2">
            <Link
              to="/comics"
              className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname === '/comics'
                || location.pathname.startsWith('/streamlined-comic/')
                || location.pathname.startsWith('/comic/')
                ? 'text-white bg-gradient-to-br from-purple-600 to-violet-600 shadow-[0_0_15px_rgba(147,51,234,0.5)] border-transparent'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
            >
              <Compass className="w-4 h-4" />
              <span>Explore</span>
            </Link>

            <Link
              to="/community"
              className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname === '/community'
                ? 'text-white bg-blue-600/40 border border-blue-500/50'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
            >
              <Users className="w-4 h-4 text-blue-400" />
              <span>Community</span>
            </Link>

            <Link
              to="/store"
              className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${location.pathname === '/store'
                ? 'text-white bg-amber-600/40 border border-amber-500/50'
                : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
            >
              <ShoppingBag className="w-4 h-4 text-amber-400" />
              <span>Store</span>
            </Link>

            {user && (
              <>
                <Link
                  to="/dashboard"
                  className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${location.pathname === '/dashboard-simple' || location.pathname === '/dashboard'
                    ? 'text-white bg-purple-600/40 border border-purple-500/50'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  <span>Creator Profile</span>
                </Link>

                <Link
                  to="/credits"
                  className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${location.pathname === '/credits'
                    ? 'text-white bg-yellow-600/40 border border-yellow-500/50'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span>Credits</span>
                </Link>

                {/* Earnings tab - dynamically enabled via admin setting */}
                {earningsEnabled ? (
                  <Link
                    to="/earnings"
                    className={`nav-link flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${location.pathname === '/earnings'
                      ? 'text-white bg-green-600/40 border border-green-500/50'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <span>Earnings</span>
                  </Link>
                ) : (
                  <div
                    className="nav-link flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-not-allowed opacity-50 text-gray-500"
                    title="Coming soon"
                  >
                    <DollarSign className="w-4 h-4" />
                    <span>Earnings</span>
                  </div>
                )}

                <Link
                  to="/create-comic"
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-semibold text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-300"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create</span>
                </Link>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-3">
            <Button asChild className="btn-primary rounded-xl">
              <Link to="/comica">Launch Comica</Link>
            </Button>
            {user ? (
              <>
                {/* Notification Bell */}
                <button
                  onClick={() => navigate('/notifications')}
                  className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5 text-gray-300 hover:text-white transition-colors" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1 animate-pulse shadow-lg shadow-red-500/50">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center space-x-2 px-3 py-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer bg-transparent border-0">
                      <Avatar className="w-8 h-8 border-2 border-purple-500/50">
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-bold">
                          {user.display_name?.charAt(0) || user.username?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden sm:block text-sm font-medium text-white">
                        {user.display_name || user.username}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 glass-card border-purple-500/30 bg-gray-900 z-[100]">
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${user.username || user.display_name}`} className="flex items-center space-x-2 w-full text-white hover:text-purple-400">
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center space-x-2 w-full text-white hover:text-purple-400">
                        <PenTool className="w-4 h-4" />
                        <span>My Comics</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout} className="flex items-center space-x-2 w-full text-red-400 hover:text-red-300">
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              location.pathname !== '/' && (
                <div className="flex items-center space-x-3">
                  <Button variant="ghost" asChild className="text-white hover:bg-white/10 rounded-xl">
                    <Link to="/login">Log In</Link>
                  </Button>
                  <Button asChild className="btn-primary">
                    <Link to="/register">Get Started</Link>
                  </Button>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
